
import { catchError, map, distinct, mergeMap, mapTo, first, mergeMapTo, filter, switchMap } from 'rxjs/operators'
import { combineEpics } from 'redux-observable'
import { Observable, of, from, empty, concat } from 'rxjs'
import { get, post } from 'helpers/observableAjaxHelpers'
import isNull from 'lodash/isNull'
import omitBy from 'lodash/omitBy'
import { GET_INSIGHTS_URL, PATCH_INSIGHTS_URL } from 'interfaces/ajax'
import { convertCTAFilterCategories, filterBadInsights, filterForbiddenCTAs, parseCta } from 'helpers/insightHelpers'
import { Action } from 'actions'
import {
  failToggleDismissInsightActionCreator,
  GET_INSIGHTS,
  INSIGHT_CTA_CLICK,
  resetInsightsActionCreator,
  setInsightsActionCreator,
  TOGGLE_DISMISS_INSIGHT,
  toggleDismissInsightActionCreator,
  UNDISMISS_INSIGHTS_BY_PLACEMENT,
  undismissInsightsByPlacementActionCreator,
  updatePlacementInsightsActionCreator,
  FETCH_CTA_FROM_URL,
  insightCtaClickActionCreator,
} from 'actions/insightsActions'
import { UPDATE_PEER_GROUP_RELATION } from 'actions/peergroupActions'
import { selectedRelationIdSelector } from 'selectors/peergroupSelectors'
import { getItemsByKey, getItemsFromCollection, normalize, setItemInCollection } from 'helpers/normalizeHelpers'
import { createTimeperiod } from 'helpers/dateTimeHelpers'
import { Epic } from 'interfaces/middleware'
import { Insight, InsightCategoriesApiFilter, InsightCTAKey } from 'interfaces/insights'
import { INSIGHT_PLACEMENTS_BY_PAGE } from 'constants/insights'
import { resolveCategory } from 'helpers/categoriesHelpers'
import { captureWarning } from 'helpers/errorHelpers'
import {
  createInsightGroupKey,
  createIsTargetInsight,
  hasAnchorInsights,
  isLinkCTA,
  isOfferCTA,
  isOpportunityInsight,
  getInsightLinkTo,
} from 'helpers/insightHelpers'
import { INSIGHT_DISCARD_TRIGGERS } from 'helpers/discardTriggerHelpers'
import { compose } from 'redux'
import { providerAccountsSelector } from 'selectors/linkageSelectors'
import { createCompositeId } from 'helpers/linkageHelpers'
import { GET_MIXED_ACCOUNTS, startLinkageSessionActionCreator } from 'actions/linkageActions'
import { MixedCompositeId } from 'interfaces/linkage'
import { State } from 'interfaces/state'
import { addAccomplishmentActionCreator } from 'actions/bountyActions'
import { insightsDataSelector } from 'selectors/insightsSelectors'
import { getStaleableData } from 'helpers/storeHelpers'
import { sortOpportunitiesActionCreator } from 'actions/opportunityActions'
import { currentPageSelector } from 'selectors/globalSelectors'
import { pushActionCreator, activateAnchorActionCreator } from 'actions/navigationActions'
import { SocialInnerInsight } from 'interfaces/socialInsights'
import { OPPORTUNITIES_URL } from 'tangle-shared'
import pick from 'lodash/pick'
import { Anchor, NavigationAppState } from 'interfaces/navigation'
import { getUrlComponents, isLocalUrl, makeUrlRelative } from 'helpers/utilsHelpers'
import { API_URL } from 'constants/api'
import { byType } from 'rxOperators/byType'
import { filterRequestOfType } from 'rxOperators/filterRequestOfType'
import { mergeMapSafe } from 'rxOperators/mergeMapSafe'
import { toDiscardCache } from 'rxOperators/toDiscardCache'
import { catchSafe } from 'rxOperators/catchSafe'
import { accountsFilterQueryParamSelector, withComparisonSelector } from 'components/features/pageFilters/selectors'


const getInsightsEpic: Epic = (action$, store) => {
  // we take both `GET_INSIGHTS` and `PAGE_CHANGED` here, compare their payload (it differs)
  // and then filter out `GET_INSIGHTS` only
  // effect: `distinct` is dropped upon any page navigation
  return action$
    .pipe(byType(GET_INSIGHTS)).pipe(
      distinct(
        ({ payload }) => JSON.stringify(payload),
        action$.pipe(toDiscardCache(INSIGHT_DISCARD_TRIGGERS)),
      ),
      switchMap(({ payload }) => {
        const { page, peersRelationId, othersRelationId } = payload

        // figure out page-specific filters (categories)
        let pageFilter: InsightCategoriesApiFilter = {}
        switch (payload.page) {
          case 'assets':
            pageFilter = { asset_category: resolveCategory(payload.category, true) }
            break
          case 'debts':
            pageFilter = { debt_category: resolveCategory(payload.category, true) }
            break
          case 'income':
            pageFilter = { income_category: resolveCategory(payload.category, true) }
            break
          case 'spending':
            pageFilter = { spending_category: resolveCategory(payload.category, true) }
            break
        }
        // don't send nulls to api
        pageFilter = omitBy(pageFilter, isNull)

        const timeperiod = createTimeperiod(payload.timeframe)
        const placements = INSIGHT_PLACEMENTS_BY_PAGE[page]
        const state = store.getState()
        const apiPayload = {
          placement: placements,
          peergroup_relation: [peersRelationId, othersRelationId],
          ...timeperiod,
          ...pageFilter,
          excluded_account_uids: accountsFilterQueryParamSelector(state),
          are_comparisons_enabled: withComparisonSelector(state),
        }

        const currentPage = currentPageSelector(store.getState(), { location: window.location })
        const isCurrentPageInsights = currentPage === page

        return get(GET_INSIGHTS_URL, apiPayload)
          .pipe(mergeMapSafe(({ response: insights }) => {
            const handledInsights = compose<Insight[], Insight[], Insight[], Insight[]>(
              convertCTAFilterCategories,
              filterBadInsights,
              filterForbiddenCTAs,
            )(insights)

            // check successor: if there is a target insight and it's dismissed we should undismiss it
            const { expectedSuccessor } = store.getState().app.insights.order
            const targetInsight = expectedSuccessor && expectedSuccessor.page === page
              ? handledInsights.find(createIsTargetInsight(expectedSuccessor))
              : undefined

            const insightsCollection = normalize(handledInsights, 'id')

            if (!targetInsight || !targetInsight.is_dismissed) {
              return of(
                setInsightsActionCreator(insightsCollection, placements, isCurrentPageInsights)
              )
            }

            const resultCollection = setItemInCollection(
              insightsCollection,
              { ...targetInsight, is_dismissed: false },
              'id',
            )
            const setInsightsAction = setInsightsActionCreator(resultCollection, placements, isCurrentPageInsights)
            const undismissAction = toggleDismissInsightActionCreator(targetInsight, false)

            return from([
              setInsightsAction,
              undismissAction,
            ])
          }))
      }))
}

const createRetryLauncher = (providerAccountCid: MixedCompositeId, action$: Observable<Action>) =>
  action$
    .pipe(filterRequestOfType(GET_MIXED_ACCOUNTS, 'success')).pipe(
      mapTo(startLinkageSessionActionCreator.request({ cid: providerAccountCid })),
      first())

const updateCredentialsEpic: Epic = (action$, store) =>
  action$
    .pipe(byType(INSIGHT_CTA_CLICK)).pipe(
      mergeMap(({ payload }) => {
        if (payload.type !== 'update_credentials') {
          return empty()
        }

        const { provider_account_id } = payload.cta
        const providerAccounts = providerAccountsSelector(store.getState())
        const cid = createCompositeId('provider-account', provider_account_id)
        // if we have `providerAccounts` it means we have been loaded them so only `retry` is needed
        if (providerAccounts) {
          return of(
            startLinkageSessionActionCreator.request({ cid }),
          )
        }

        // if don't wait when our account will become linked (when loaded)
        // and then launch `retry`
        return createRetryLauncher(cid, action$)
      }))

const dismissInsightEpic: Epic = (action$) =>
  action$
    .pipe(byType(TOGGLE_DISMISS_INSIGHT)).pipe(
      mergeMap(({ payload }) => {
        const { insight, is_dismissed } = payload

        return post(
          PATCH_INSIGHTS_URL,
          {
            query: { id: [insight.id] },
            body: { is_dismissed },
          },
        ).pipe(
          mergeMapTo(empty()))
          .pipe(catchSafe(() =>
            of(
              failToggleDismissInsightActionCreator(insight, is_dismissed),
            ),
          ))
      }))

const undismissInsightsEpic: Epic = (action$, store) =>
  action$
    .pipe(filterRequestOfType(UNDISMISS_INSIGHTS_BY_PLACEMENT, 'request')).pipe(
      mergeMap((action) => {
        const { placement, insights, silent } = action.payload.params
        const ids = (insights as (Insight | SocialInnerInsight)[]).map(({ id }) => id)
        const state: State = store.getState()
        const key = createInsightGroupKey(placement)

        if (!state.data.insights || !state.data.insights[key]) {
          throw new Error(`Can't handle ${ UNDISMISS_INSIGHTS_BY_PLACEMENT } when no insights loaded`)
        }

        const allInsights = getStaleableData(state.data.insights[key])

        if (!allInsights) {
          throw new Error(`Can't handle ${ UNDISMISS_INSIGHTS_BY_PLACEMENT } when no insights loaded`)
        }

        const updatedInsights = allInsights.allKeys
          .map((id) => ({
            ...allInsights.byKey[id],
            is_dismissed: false,
          }))
        const updatedInsightsCollection = normalize<Insight | SocialInnerInsight, 'id'>(updatedInsights, 'id')

        const request$ = post(
          PATCH_INSIGHTS_URL,
          {
            query: { id: ids },
            body: { is_dismissed: false },
          },
        ).pipe(
          mergeMapTo(empty()))
          .pipe(catchSafe(() => {
            const failedInsights = getItemsByKey<Insight | SocialInnerInsight>(allInsights.byKey, ids)

            // TODO handle failure
            return of(
              undismissInsightsByPlacementActionCreator.fail({
                placement,
                insights: failedInsights,
              }),
            )
          }))

        // if silent then we don't update insights
        if (silent) {
          return request$
        }

        const update$ = of(
          updatePlacementInsightsActionCreator(placement, updatedInsightsCollection),
        )

        return concat(
          update$,
          request$,
        )
      }))

const handleCTAClickEpic: Epic = (action$) =>
  action$
    .pipe(byType(INSIGHT_CTA_CLICK)).pipe(
      filter(({ payload }) => !!payload.insight && !!payload.insight.reward && isOfferCTA(payload.type)),
      mergeMap(() =>
        of(addAccomplishmentActionCreator({ slug: 'reward_is_tracking' }))
      ))

const handleCTAFiltersEpic: Epic = (action$, store) =>
  action$
    .pipe(byType(INSIGHT_CTA_CLICK)).pipe(
      mergeMap(({ payload }) => {
        const { cta, options: { isCausingInsightsReset }} = payload
        const state = store.getState()
        const insightsData = insightsDataSelector(state)

        if (isCausingInsightsReset || !isLinkCTA(cta) || !cta.successor || !insightsData || !hasAnchorInsights(cta.target)) {
          return empty()
        }

        const placement = cta.target

        if (!insightsData[placement]) {
          return empty()
        }

        const insights = getStaleableData(insightsData[placement])

        if (!insights) {
          return empty()
        }

        const insightItems: Insight[] = getItemsFromCollection(insights)
        const isTargetInsight = createIsTargetInsight(cta.successor)
        const targetInsight = insightItems.find(isTargetInsight)

        if (!targetInsight) {
          return empty()
        }

        const actions: Action[] = []

        if (targetInsight.is_dismissed) {
          const resultCollection = setItemInCollection(
            insights,
            { ...targetInsight, is_dismissed: false },
            'id',
          )
          const undismissAction = toggleDismissInsightActionCreator(targetInsight, false)
          actions.push(undismissAction)

          if (isOpportunityInsight(targetInsight)) {
            actions.push(sortOpportunitiesActionCreator(resultCollection as any)) // TODO
          }
        } else {
          actions.push(updatePlacementInsightsActionCreator(placement, insights))
        }

        return from(actions)
      }))

// cover all cases that can't be covered by discard triggers
const resetInsightsEpic: Epic = (action$, store) =>
  action$
    .pipe(byType(UPDATE_PEER_GROUP_RELATION))
    .pipe(mergeMapSafe((a) => {
      const relationId = a.payload.id
      const state = store.getState()
      const selectedRelationId = selectedRelationIdSelector(state)

      if (relationId === selectedRelationId) {
        return of(
          resetInsightsActionCreator({ priorRelationId: relationId }),
        )
      }

      return empty()
    }))

const linkEpic: Epic = (action$) =>
  action$.pipe(
    byType(FETCH_CTA_FROM_URL),
    mergeMap(() => of(window.location).pipe(
      mergeMap((location) => {
        const fromFragment = parseCta(location.hash.replace(/^#/, ''), true)
        if (fromFragment) {
          return of({
            type: 'fragment' as const,
            cta: fromFragment,
          })
        }

        // mimic /perform-cta?cta= url
        const fromSearch = parseCta(location.search, false)
        if (fromSearch) {
          return of({
            type: 'perform-cta' as const,
            cta: fromSearch,
          })
        }

        return empty()
      }),
      map(({ cta, type }) => {
        const ctaName = Object.keys(cta)[0] as InsightCTAKey
        const description = getInsightLinkTo(pick(cta, ctaName))
        return {
          cta,
          type,
          ctaName,
          description,
        }
      }),
      mergeMap((event) => {
        const { description } = event
        let { url } = description

        if (description.type === 'none') {
          return of(event)
        }

        const urlComponents = getUrlComponents(url)
        if (urlComponents && isLocalUrl(urlComponents)) {
          // absolute url to the current host
          url = makeUrlRelative(urlComponents)
          if (url.startsWith(API_URL)) {
            // url to API (most likely redirect)
            location.href = url
            return empty()
          }

        } else if (urlComponents) {
          // absolute url to the third-party host
          location.href = url
          return empty()
        }

        return of(event)
      }),
      mergeMap(({ cta, type, ctaName, description }) => {
        let activateAnchor: Anchor | null = null
        let redirectTo: NavigationAppState['navigateToUrl'] = null
        let clickAction: Action | null = null

        if (description.type !== 'none') {
          clickAction = insightCtaClickActionCreator(ctaName, cta[ctaName] || {}) as Action

          if (description.type === 'anchor') {
            activateAnchor = description.to
          }

          if (description.type === 'url' && description.search) {
            redirectTo = {
              pathname: description.url,
              search: `?${ new URLSearchParams(description.search).toString() }`,
            }
          } else {
            redirectTo = description.url
          }

        }

        if (type === 'perform-cta' && !redirectTo) {
          redirectTo = OPPORTUNITIES_URL
        }

        const actions = [
          ...redirectTo ? [pushActionCreator(redirectTo)] : [],
          ...activateAnchor ? [activateAnchorActionCreator(activateAnchor)] : [],
          ...clickAction ? [clickAction] : [],
        ]

        return of(...actions)
      }),
      catchError((error) => {
        captureWarning('Wrong CTA link', error)
        return empty()
      }),

    ))
  )


export default combineEpics(
  getInsightsEpic,
  handleCTAClickEpic,
  handleCTAFiltersEpic,
  updateCredentialsEpic,
  dismissInsightEpic,
  undismissInsightsEpic,
  resetInsightsEpic,
  linkEpic,
)
